import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import StepChild from "@/components/wizard/StepChild";
import StepTheme from "@/components/wizard/StepTheme";
import StepStyle from "@/components/wizard/StepStyle";
import StepReview from "@/components/wizard/StepReview";

const STEP_LABELS = ["Child", "Story", "Style", "Preview"];

function isChildValid(c) {
  return c.name.trim().length > 0 && c.age >= 2 && c.age <= 12;
}

export default function Wizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [child, setChild] = useState({
    name: "", age: 5, gender: "Kid", skin_tone: "light",
    hair: "short curly brown", favorite_thing: "",
  });
  const [story, setStory] = useState({
    theme: "bedtime", custom_prompt: "", illustration_style: "watercolor",
  });

  const canNext = () => {
    if (step === 1) return isChildValid(child);
    if (step === 2) return !!story.theme;
    if (step === 3) return !!story.illustration_style;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await http.post("/preview", { child, story });
      sessionStorage.setItem("sf_preview", JSON.stringify({ ...data, child, story }));
      nav("/preview");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't build your preview — try again.";
      toast.error(String(msg).slice(0, 220));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-3xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-3 text-xs font-semibold uppercase tracking-widest text-[#5C6370]">
          <span>Step {step} of 4</span>
          <span>{STEP_LABELS[step - 1]}</span>
        </div>
        <Progress value={(step / 4) * 100} className="h-2 bg-[#F2CC8F]/30" data-testid="wizard-progress" />

        <div className="paper-card mt-6 p-6 sm:p-10">
          {step === 1 && <StepChild child={child} setChild={setChild} />}
          {step === 2 && <StepTheme story={story} setStory={setStory} />}
          {step === 3 && <StepStyle story={story} setStory={setStory} />}
          {step === 4 && (
            <StepReview child={child} story={story} loading={loading} onSubmit={submit} />
          )}

          <div className="mt-8 flex items-center justify-between">
            <Button variant="ghost" disabled={step === 1 || loading} onClick={() => setStep(step - 1)}
              data-testid="wizard-back" className="text-[#4A4E69]">
              <ArrowLeft size={16} className="mr-1" /> Back
            </Button>
            {step < 4 && (
              <Button data-testid="wizard-next" disabled={!canNext()} onClick={() => setStep(step + 1)}
                className="bg-[#2B2D42] hover:bg-[#3D405B] text-white rounded-full px-6">
                Next <ArrowRight size={16} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
